// ═══════════════════════════════════════════════════════════════════════════
// PasswordResetConfirmPage — Phase 2.2 step 2.
//
// User lands here from the email recovery link. Supabase auto-detects the
// recovery token in the URL fragment via `detectSessionInUrl: true` (see
// lib/supabase/client.ts) and establishes a temporary session that lets us
// call `updateUser({ password })` without re-authenticating.
//
// Behaviour:
//   - validates the new password client-side (min 8 chars + at least one
//     letter and one digit)
//   - shows confirm-match feedback as the user types the second field
//   - on success, signs the user out and redirects to /auth/login so they
//     re-authenticate with the new credentials (avoids leaving a stale
//     recovery session lying around)
//   - on PASSWORD_RECOVERY events that ARRIVE without a session (e.g. the
//     link expired), redirects back to the request page with a hint
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset } from '../../services/authApi';
import { mapAuthError } from '../../services/errorMap';
import { analytics } from '../../services/analytics';
import { supabase } from '../../lib/supabase/client';
import { PasswordStrengthMeter } from '../../components/auth/PasswordStrengthMeter';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { PASSWORD_RECOVERY_TIMEOUT_MS } from '../../config/authTimeouts';
import styles from './auth-page.module.css';

const MIN_PASSWORD_LENGTH = 8;

type Status = 'idle' | 'submitting' | 'done' | 'error';

function passwordPolicyOk(password: string): boolean {
  if (password.length < MIN_PASSWORD_LENGTH) return false;
  // Require at least one letter AND one digit. Symbol is recommended but
  // not enforced here — zxcvbn meter on the form gives the user the full
  // feedback. Hard-blocking on a regex would frustrate users with strong
  // passphrases that don't contain digits.
  return /[a-zA-Z]/.test(password) && /\d/.test(password);
}

export function PasswordResetConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasRecoverySession, setHasRecoverySession] = useState(true);
  // PasswordStrengthMeter callback. We require a zxcvbn score ≥ 3
  // (threshold) on top of the local letter+digit policy below — the
  // meter does the real work, the regex catches obvious typos faster.
  const [meterStrong, setMeterStrong] = useState(false);

  // Watch for the PASSWORD_RECOVERY event that Supabase fires once
  // `detectSessionInUrl` finishes parsing. If no recovery session
  // materialises within a reasonable window, the link was probably
  // expired or already consumed — bounce back to step 1.
  useEffect(() => {
    let recovered = false;
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        recovered = true;
      }
    });
    // Give Supabase ~3s to finish processing the URL fragment. If nothing
    // happened by then, assume the link is dead.
    const timer = window.setTimeout(() => {
      if (!recovered) {
        // Best-effort: the user might still have a session from earlier.
        // We only flip to "no recovery" if `getSession()` confirms it.
        supabase.auth.getSession().then(({ data }) => {
          if (!data?.session) setHasRecoverySession(false);
        });
      }
    }, PASSWORD_RECOVERY_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
      subscription?.subscription?.unsubscribe?.();
    };
  }, []);

  const passwordsMatch = useMemo(() => {
    if (!confirmPassword) return null;
    return password === confirmPassword;
  }, [password, confirmPassword]);

  const policyOk = useMemo(() => passwordPolicyOk(password), [password]);

  const canSubmit =
    status !== 'submitting' && policyOk && meterStrong && passwordsMatch === true;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setError(null);
    analytics.event('auth_password_reset_confirm_request');
    try {
      await confirmPasswordReset(password);
      analytics.event('auth_password_reset_confirm_success');
      // Force a clean re-login so the recovery session can't linger.
      await supabase.auth.signOut().catch(() => {});
      setStatus('done');
      // No automatic redirect — the success state shows an explicit
      // "Continue" button so the user controls when to leave the page.
      // Replaces a brittle 1.5s setTimeout that races with screen readers
      // and confused users on slow connections.
    } catch (err) {
      const mapped = mapAuthError(err);
      setError(t(mapped.messageKey, mapped.messageDefault));
      setStatus('error');
      analytics.event('auth_password_reset_confirm_failed', { code: mapped.code });
    }
  };

  if (!hasRecoverySession) {
    return (
      <AuthLayout cardMaxWidth={420} cardPadding="32px" background="var(--bg)">
        <div className={styles.doneStack}>
          <h1 className={styles.title}>
            {t('auth.passwordResetExpiredTitle', { defaultValue: 'Lien expiré' })}
          </h1>
          <p className={styles.subtitle}>
            {t('auth.passwordResetExpiredBody', {
              defaultValue:
                "Ton lien de réinitialisation n'est plus valide. Demande-en un nouveau.",
            })}
          </p>
          <Link to="/auth/forgot-password" className={styles.primaryButton}>
            {t('auth.actions.reset', 'Reset password')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout cardMaxWidth={420} cardPadding="32px" background="var(--bg)">
      <h1 className={styles.title}>
        {t('auth.passwordResetConfirmTitle', { defaultValue: 'Nouveau mot de passe' })}
      </h1>
      <p className={styles.subtitle}>
        {t('auth.passwordResetConfirmSubtitle', {
          defaultValue: 'Choisis un nouveau mot de passe pour ton compte.',
        })}
      </p>

      {status === 'done' ? (
        <div role="status" aria-live="polite" className={styles.doneStack}>
          <p className={styles.successText}>
            ✓ {t('auth.passwordResetDone', { defaultValue: 'Mot de passe mis à jour.' })}
          </p>
          <button
            type="button"
            autoFocus
            onClick={() => navigate('/auth/login', { replace: true })}
            className={styles.primaryButton}
          >
            {t('auth.actions.continue', { defaultValue: 'Continue' })}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <label htmlFor="new-password" className={styles.fieldLabel}>
            {t('auth.password', 'Mot de passe')}
          </label>
          <input
            id="new-password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={status === 'submitting'}
            className={styles.inputCompact}
          />
          {/* zxcvbn-based meter. Renders nothing for empty input, then
              shows a coloured bar + suggestions as the user types.
              `onStrengthChange` returns whether the score reaches the
              meter's threshold (default 3) — we gate submission on it. */}
          <div className={styles.meterSlot}>
            <PasswordStrengthMeter
              password={password}
              onStrengthChange={(_score, isValid) => setMeterStrong(isValid)}
            />
          </div>

          <label htmlFor="confirm-password" className={styles.fieldLabel}>
            {t('auth.confirmPassword', 'Confirmer')}
          </label>
          <input
            id="confirm-password"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={status === 'submitting'}
            aria-invalid={passwordsMatch === false}
            className={passwordsMatch === false ? styles.inputInvalid : styles.inputCompact}
          />
          {passwordsMatch === false && (
            <p role="alert" className={styles.errorTextInline}>
              {t('auth.passwordMismatch', 'Les mots de passe ne correspondent pas')}
            </p>
          )}
          {!policyOk && password.length > 0 && (
            <p className={styles.policyHint}>
              {t('auth.passwordTooShort', 'Le mot de passe doit contenir au moins 8 caractères')}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            aria-busy={status === 'submitting'}
            className={styles.primaryButton}
          >
            {status === 'submitting' ? t('common.loading', 'Loading…') : t('auth.actions.reset', 'Reset password')}
          </button>

          {error && (
            <p role="alert" className={styles.errorText}>
              {error}
            </p>
          )}
        </form>
      )}
    </AuthLayout>
  );
}

export default PasswordResetConfirmPage;
